import { useQuery, type QueryClient } from '@tanstack/react-query'
import { dispatchData } from '@/api/dispatch'
import { request } from '@/api/client'
import { rowsOf } from '@/domain/extract'
import type { StatusOption } from '@/domain/models/shipment'
import { authService } from './authService'

/** Dispatch reference lists share one shape: `{ id, englishName, arabicName }` (id may be string or number). */
export interface LookupItem {
  id: string | number
  englishName: string
  arabicName: string
  mainReasonId?: number
  /** Cities only: the code Dispatch's Excel importer reads ("CAI", "ALX"); from bncGeoCity/GetPage. */
  code?: string
  /** Runners only: the branch he belongs to, so a picker can show it and a screen can filter by it. */
  branch?: string
  branchId?: number
  /** Clients only: Dispatch still holds the account but it is closed — searchable, not offered for new work. */
  inactive?: boolean
}

/** Reference data changes rarely: cached for an hour and shared by every screen. */
const REFERENCE = { staleTime: 60 * 60_000, gcTime: 2 * 60 * 60_000 }
const PAGE = { pageSize: 5000, pageNumber: 1 }

type PostPath = Parameters<typeof dispatchData<unknown, 'post', '/api/Status/GetList'>>[1] | (string & {})
const list = async (path: PostPath, body: unknown, signal?: AbortSignal) =>
  rowsOf<LookupItem>(await dispatchData<unknown, 'post', '/api/Status/GetList'>('post', path as '/api/Status/GetList', { body: body as never, signal }))

export const lookupService = {
  statuses: (signal?: AbortSignal) => list('/api/Status/GetList', PAGE, signal) as Promise<StatusOption[]>,
  /**
   * Every status. Status/GetList answers only 10 (1-10, live 2026-09-19); Status/GetPage has all 22 (11 Re-Operate,
   * 12 Picked Up, 16 / 17 refusals, 45, 47, 48, 49, 52 ...). GetPage first, GetList added for anything it misses (or
   * alone when GetPage is refused).
   */
  async statusCatalogue(signal?: AbortSignal): Promise<LookupItem[]> {
    const short = await list('/api/Status/GetList', PAGE, signal).catch(() => [] as LookupItem[])
    let full: LookupItem[] = []
    try {
      full = named(await dispatchData<unknown, 'post', '/api/Status/GetPage'>('post', '/api/Status/GetPage', { body: { fromDate: '2000-01-01T00:00:00', toDate: '2100-12-31T23:59:59', pageParam: { pageSize: 1000, pageNumber: 1 } } as never, signal }), 'statusId')
    } catch {
      if (!short.length) throw new Error('Status list unavailable')
    }
    const byId = new Map<number, LookupItem>()
    for (const s of [...short, ...full]) if (Number(s.id) > 0) byId.set(Number(s.id), { ...s, id: Number(s.id) })
    return [...byId.values()].sort((a, b) => Number(a.id) - Number(b.id))
  },
  reasons: (signal?: AbortSignal) => list('/api/Reasons/GetList', PAGE, signal),
  mainReasons: (signal?: AbortSignal) => list('/api/Reasons/GetMainReasonsList', PAGE, signal),
  /**
   * Every client account, closed ones included, each carrying `inactive`.
   *
   * `Clients/GetList` answers a 10-row stub on this account, and `Clients/GetPage` was asked for a **single** page:
   * a page size that looks generous is still one page, and nothing in the answer says it was cut. The gateway pages
   * through it on its own account and caches the result; Dispatch's own call stays as the fallback, so a refusal
   * never leaves a picker blank.
   */
  async clientsAll(signal?: AbortSignal): Promise<LookupItem[]> {
    try {
      const { data } = await request<{ rows: { id: number; name: string; englishName?: string; inactive?: boolean }[] }>('/ext/clients', { signal })
      const rows = (data?.rows ?? []).map((c): LookupItem => ({ id: c.id, arabicName: c.name, englishName: c.englishName || c.name, inactive: c.inactive === true }))
      if (rows.length) return rows
    } catch {
      /* falls through to Dispatch's own list below */
    }
    const rows = rowsOf<{ fId: number; fName: string; fNameA: string; inActive?: boolean }>(await dispatchData<unknown, 'post', '/api/Clients/GetPage'>('post', '/api/Clients/GetPage', { body: { pageParam: { pageSize: 3000, pageNumber: 1 } } as never, signal }))
    return rows.map((r): LookupItem => ({ id: r.fId, englishName: r.fName || r.fNameA, arabicName: r.fNameA || r.fName, inactive: r.inActive === true })).sort((a, b) => a.arabicName.localeCompare(b.arabicName, 'ar'))
  },
  /** The working accounts as Dispatch holds them now (at most 30 s old), for screens that set up a client who may have just signed up. */
  async clientsFresh(signal?: AbortSignal): Promise<LookupItem[]> {
    const { data } = await request<{ rows: { id: number; name: string; englishName?: string; inactive?: boolean }[] }>('/ext/clients', { query: { fresh: '1' }, signal })
    return (data?.rows ?? []).filter((c) => c.inactive !== true).map((c): LookupItem => ({ id: c.id, arabicName: c.name, englishName: c.englishName || c.name }))
  },
  /** The accounts you may start new work for. A closed account stays searchable through `clientsAll`, never here. */
  async clients(signal?: AbortSignal): Promise<LookupItem[]> {
    return (await lookupService.clientsAll(signal)).filter((c) => !c.inactive)
  },
  cities: (signal?: AbortSignal) => list('/api/Zones/GetCityList', PAGE, signal),
  /**
   * The same 46 cities with their codes. Zones/GetCityList has no code and bncGeoCity/GetList answers 403 on the
   * service account, so the page endpoint is the one that works (verified live 2026-09-20).
   */
  async cityCodes(signal?: AbortSignal): Promise<LookupItem[]> {
    try {
      const data = await dispatchData<unknown, 'post', '/api/bncGeoCity/GetPage'>('post', '/api/bncGeoCity/GetPage', { body: { pageParam: { pageSize: 500, pageNumber: 1 } } as never, signal })
      const rows = rowsOf<{ cityId: number; code?: string; fName?: string; fNameA?: string }>(data)
        .filter((c) => Number(c.cityId) > 0)
        .map((c) => ({ id: c.cityId, englishName: String(c.fName ?? c.fNameA ?? ''), arabicName: String(c.fNameA ?? c.fName ?? ''), code: String(c.code ?? '').trim() }))
      if (rows.length) return rows
    } catch (error) {
      if ((error as { name?: string }).name === 'AbortError') throw error
    }
    /* bncGeoCity is master data: the endpoint table guards it with `masterData:read`, which an employee whose
       permissions come from his Dispatch screen authorisations need not have — and an empty list silently turns
       every row of an Excel upload into "needs a city". The gateway serves the same rows off its own account. */
    return (await request<LookupItem[]>('/ext/cities/codes', { signal })).data
  },
  /**
   * Every delivery runner, of every branch.
   *
   * Dispatch has no "all runners" call: `Runners/GetList` answers with the ten system users, not the couriers
   * (ka3bo/sync.ts, lineexpress/sync.ts and portalPickups.ts all read per branch instead), so a picker built on it
   * misses whole branches. The gateway reads `Runners/GetPageByBranchID` per branch on its own account and merges
   * them; `Runners/GetList` stays only as the fallback, so a refusal never empties a picker.
   */
  async runners(signal?: AbortSignal): Promise<LookupItem[]> {
    try {
      const { data } = await request<{ rows: { id: number; name: string; englishName?: string; branch?: string; branchId?: number }[] }>('/ext/runners', { signal })
      const rows = (data?.rows ?? []).map((r): LookupItem => ({ id: r.id, arabicName: r.name, englishName: r.englishName || r.name, branch: r.branch ?? '', branchId: r.branchId ?? 0 }))
      if (rows.length) return rows
    } catch {
      /* falls through to Dispatch's own list below */
    }
    return list('/api/Runners/GetList', { ...PAGE, branchID: 0 }, signal)
  },
  async branches(signal?: AbortSignal): Promise<LookupItem[]> {
    try { return await list('/api/Branches/GetList', { ...PAGE, branchType: 0 }, signal) }
    catch (error) {
      // Shipment operators may choose their own authorised branches without access to branch administration.
      if ((error as { status?: number }).status !== 403) throw error
      const allowed = await authService.branches()
      return allowed.map(b => ({ id: Number(b.branchID ?? b.branchId ?? b.id), englishName: String(b.branchName ?? b.englishName ?? b.name ?? ''), arabicName: String(b.branchNameA ?? b.arabicName ?? b.branchName ?? b.name ?? '') })).filter(b => b.id > 0)
    }
  },
  cells: (signal?: AbortSignal) => list('/api/Cells/GetList', PAGE, signal),
  shipmentTypes: (signal?: AbortSignal) => list('/api/Shipments/GetShipmentTypes', PAGE, signal),
  pickupStatuses: (signal?: AbortSignal) => list('/api/PickupsManagement/GetStatusList', PAGE, signal),
  async deliveryStatuses(signal?: AbortSignal): Promise<LookupItem[]> {
    const data = await dispatchData<unknown, 'get', '/api/VisitResult/GetDeliveryStatus'>('get', '/api/VisitResult/GetDeliveryStatus', { signal })
    return rowsOf<{ statusId: number; fName: string; fNameA: string }>(data).map((s) => ({ id: s.statusId, englishName: s.fName, arabicName: s.fNameA }))
  },
  /** Delivery types of a delivered visit result (VisitResultDTL.recipientId): 1 full, 2 partial, 3 full with price edit, 5 exchange, 6 pay and get, 7 pick up. */
  deliveryTypes: (signal?: AbortSignal) => list('/api/VisitResult/GetDeliveryTypesList', PAGE, signal),
  async im2Clients(signal?: AbortSignal): Promise<LookupItem[]> {
    const data = await dispatchData<unknown, 'get', '/api/IM2Clients/V1/GetAll'>('get', '/api/IM2Clients/V1/GetAll', { signal })
    return rowsOf<{ clientId: number; englishName: string; arabicName: string }>(data).map((c) => ({ id: c.clientId, englishName: c.englishName, arabicName: c.arabicName }))
  },
  // ---- lists behind the legacy VSoft report / call-center / admin filters (verified shapes 2026-09-18) ----
  /** Dispatch web users (User filter of AWB Status Tracking, Data Entry, Call Center …): `{ userId, dispalyName, username }`. */
  async users(signal?: AbortSignal): Promise<LookupItem[]> {
    const data = await dispatchData<unknown, 'get', '/api/Users/V1/GetUsers'>('get', '/api/Users/V1/GetUsers', { signal })
    return rowsOf<{ userId: number; username: string; dispalyName?: string }>(data).map((u) => ({ id: u.userId, englishName: u.dispalyName || u.username, arabicName: u.dispalyName || u.username }))
  },
  callTypes: (signal?: AbortSignal) => list('/api/CallCenter/GetCallTypesList', PAGE, signal),
  callStatuses: (signal?: AbortSignal) => list('/api/CallCenter/GetCallStatus', PAGE, signal),
  callResults: (signal?: AbortSignal) => list('/api/CallCenter/GetCallResultList', PAGE, signal),
  csTypes: async (signal?: AbortSignal) => named(await dispatchData<unknown, 'post', '/api/ContactUsTypes/GetList'>('post', '/api/ContactUsTypes/GetList', { body: PAGE, signal }), 'contactUsTypeId'),
  csStatuses: async (signal?: AbortSignal) => named(await dispatchData<unknown, 'post', '/api/ContactUsStatuses/GetList'>('post', '/api/ContactUsStatuses/GetList', { body: PAGE, signal }), 'contactUsStatusId'),
  transactionTypes: (signal?: AbortSignal) => list('/api/Shipments/GetTransactionsTypes', PAGE, signal),
  async auditCategories(signal?: AbortSignal): Promise<LookupItem[]> {
    const data = await dispatchData<unknown, 'get', '/api/ReportsAndInquiry/GetAuditCategories'>('get', '/api/ReportsAndInquiry/GetAuditCategories', { signal })
    return named(data, 'categoryId', 'id', 'categoryID')
  },
  async im2Stores(signal?: AbortSignal): Promise<LookupItem[]> {
    const data = await dispatchData<unknown, 'get', '/api/IM2Stores/V1/GetAll'>('get', '/api/IM2Stores/V1/GetAll', { signal })
    return named(data, 'storeId', 'id')
  },
  /** Company or individual: kept in the client's own Dispatch field accountType (free text there, empty for every
   *  client until 2026-09-22), so it stays on the client and shows in Dispatch's "Account Type" column. */
  accountTypes: async (): Promise<LookupItem[]> => ACCOUNT_TYPES,
  /** Price lists, for picking a client's list (the client edit form showed a bare id before). */
  async priceLists(signal?: AbortSignal): Promise<LookupItem[]> {
    const data = await dispatchData<unknown, 'post', '/api/PriceLists/GetList'>('post', '/api/PriceLists/GetList', { body: { pageSize: 500, pageNumber: 1 } as never, signal })
    return named(data, 'priceListId', 'priceListID', 'id')
  },
  /** Products (bncProducts), e.g. a client's pickup product. */
  async products(signal?: AbortSignal): Promise<LookupItem[]> {
    const data = await dispatchData<unknown, 'post', '/api/bncProducts/GetPage'>('post', '/api/bncProducts/GetPage', { body: { fromDate: '2000-01-01T00:00:00', toDate: '2100-12-31T23:59:59', pageParam: { pageSize: 500, pageNumber: 1 } } as never, signal })
    return named(data, 'productId', 'productID', 'id')
  },
  async recipients(signal?: AbortSignal): Promise<LookupItem[]> {
    const data = await dispatchData<unknown, 'post', '/api/Receipts/GetList'>('post', '/api/Receipts/GetList', { body: PAGE, signal })
    return named(data, 'recipientId', 'id', 'recipientID')
  },
}

export const ACCOUNT_TYPES: LookupItem[] = [
  { id: 'شركة', englishName: 'Company', arabicName: 'شركة' },
  { id: 'فرد', englishName: 'Individual', arabicName: 'فرد' },
]

/** Lists that come back as `{ <idKey>, fName, fNameA }` or `{ id, englishName, arabicName }` — normalised to one shape. */
function named(data: unknown, ...idKeys: string[]): LookupItem[] {
  return rowsOf<Record<string, unknown>>(data)
    .map((r) => {
      const id = idKeys.map((k) => r[k]).find((v) => v !== undefined && v !== null) ?? r.id
      const en = String(r.englishName ?? r.fName ?? r.name ?? r.storeName ?? r.categoryName ?? '')
      const ar = String(r.arabicName ?? r.fNameA ?? r.nameA ?? en)
      return { id: id as string | number, englishName: en || ar, arabicName: ar || en }
    })
    .filter((i) => i.id !== undefined && i.id !== null)
}

export type LookupKey =
  | 'statuses' | 'statusCatalogue' | 'reasons' | 'mainReasons' | 'clients' | 'clientsAll' | 'cities' | 'cityCodes' | 'runners' | 'branches' | 'cells' | 'shipmentTypes' | 'pickupStatuses' | 'im2Clients' | 'deliveryStatuses' | 'deliveryTypes'
  | 'users' | 'callTypes' | 'callStatuses' | 'callResults' | 'csTypes' | 'csStatuses' | 'transactionTypes' | 'auditCategories' | 'im2Stores' | 'recipients' | 'priceLists' | 'products' | 'accountTypes'

/** Loads a reference list ahead of time (same key and freshness as useLookup), e.g. before a form that needs it opens. */
export function prefetchLookup(qc: QueryClient, key: LookupKey) {
  return qc.prefetchQuery({ queryKey: ['lookup', key], queryFn: ({ signal }) => (lookupService[key] as (s?: AbortSignal) => Promise<LookupItem[]>)(signal), ...REFERENCE })
}

export function useLookup(key: LookupKey, enabled = true) {
  return useQuery({ queryKey: ['lookup', key], queryFn: ({ signal }) => (lookupService[key] as (s?: AbortSignal) => Promise<LookupItem[]>)(signal), enabled, ...REFERENCE })
}

export const useStatuses = () => useLookup('statuses') as ReturnType<typeof useQuery<StatusOption[]>>

/** Localised label for a lookup item. */
export const lookupLabel = (item: LookupItem | undefined, locale: 'ar' | 'en') => (item ? (locale === 'ar' ? item.arabicName || item.englishName : item.englishName || item.arabicName) : '')
